<script lang="ts" setup>
import { ElNotification } from "element-plus";
import { ref } from "vue";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { useOrganizationStore } from "~/projects/crm/stores/organizationStore";
import { Organization } from "~/projects/crm/types";
import { Close, Plus } from "@element-plus/icons-vue";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";

definePageMeta({
  layout: false,
});
const props = defineProps({
  showEditModalHeader: {
    type: Boolean,
    default: true,
  },
});

const organizationStore = useOrganizationStore();
const configStore = useConfigStore();

const formRef = ref<ElementUIFormRef | null>(null);
const formData = reactive({
  address_info: {},
} as Partial<Organization>);

const validateWebsite = function (
  rule: FormRuleType,
  value: string,
  callback: (arg0?: Error) => void
) {
  if (value !== undefined && value !== "") {
    if (!value.startsWith("http://") && !value.startsWith("https://")) {
      callback(new Error("Give HTTP or HTTPS URL"));
    }
  }
  callback();
};

const rules = reactive<FormRuleType>({
  name: [
    {
      required: true,
      message: "Name is required",
      trigger: "blur",
    },
  ],
  no_of_employees: [
    {
      required: true,
      message: "No. of employee is required",
      trigger: "blur",
    },
  ],
  description: [
    {
      required: false,
      trigger: "blur",
    },
  ],
  website: [
    {
      validator: validateWebsite,
      message: "Give HTTP or HTTPS URL",
      trigger: "blur",
    },
  ],
});

const multipartFormData = new FormData();
const fileInputRef = ref();

const handleFileChange = (event: any) => {
  const file = event.target.files[0];
  multipartFormData.set("logo", file);
};

const createOrganization = function () {
  if (!formRef.value) return;
  formRef.value?.validate(async (valid: any) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
    } 
    else if (valid) {
      for (const [key, value] of Object.entries(formData)) {
        if (value != null) {
          multipartFormData.set(key, value);
        }
      }

      multipartFormData.set(
        "address_info",
        JSON.stringify(formData.address_info)
      );

      const lead = await organizationStore.createOrganization(
        multipartFormData
      );
      if (lead) {
        ElNotification({
          title: "Organization created",
          message: "New Organization created",
          type: "success",
        });
        organizationStore.setCreateModalVisibility(false);

        await organizationStore.getOrganizations();
        await organizationStore.getSimpleOrganizations();
      }
    } else {
      console.log("error in validation");
    }
  });
};
</script>
<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title> Create Organization</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData"
    class="space-y-5"
    :rules="rules"
    @submit.prevent="createOrganization"
  >
  <div class="feature-filter-grid-1">
    <el-form-item class="form-elem-container" prop="name">
      <div class="form-elem-container">
        <FormLabel :required="true">Name</FormLabel>
        <el-input
          v-model="formData.name"
          clearable
          placeholder="Please input name"
        />
      </div>
    </el-form-item>

    <el-form-item class="form-elem-container" prop="industry">
      <div class="form-elem-container">
        <FormLabel>Category</FormLabel>
        <el-select
          v-model="formData.category_id"
          filterable
          class="w-full"
          clearable
          placeholder="Please select category"
        >
          <el-option
            v-for="category in configStore.globalConfig.organization_categories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
          ></el-option>
        </el-select>
      </div>
    </el-form-item>

    <el-form-item class="form-elem-container" prop="no_of_employees">
      <div class="form-elem-container">
        <FormLabel :required="true"> No. of employees</FormLabel>
        <el-input v-model="formData.no_of_employees" type="number" />
      </div>
    </el-form-item>

    <el-form-item class="form-elem-container" prop="address">
      <div class="form-elem-container">
        <FormLabel>Address</FormLabel>
        <el-input
          v-model="formData.address_info.address"
          type="textarea"
          resize="vertical"
          :rows="1"
        />
      </div>
    </el-form-item>
    <el-form-item class="form-elem-container">
      <div class="form-elem-container">
        <FormLabel>Division</FormLabel>
        <el-input
          v-model="formData.address_info.division"
          class="w-full"
          clearable
        />
      </div>
    </el-form-item>
    <el-form-item class="form-elem-container">
      <div class="form-elem-container">
        <FormLabel>City</FormLabel>
        <el-input
          v-model="formData.address_info.city"
          class="w-full"
          clearable
        />
      </div>
    </el-form-item>
    <el-form-item class="form-elem-container">
      <div class="form-elem-container">
        <FormLabel>Post Code</FormLabel>
        <el-input
          v-model="formData.address_info.postcode"
          class="w-full"
          type="number"
          clearable
        />
      </div>
    </el-form-item>
    <el-form-item class="form-elem-container" prop="website">
      <div class="form-elem-container">
        <FormLabel>Website</FormLabel>
        <el-input
          v-model="formData.website"
          clearable
          placeholder="Please input website"
        />
      </div>
    </el-form-item>
    <el-form-item class="form-elem-container" prop="logo">
      <div class="form-elem-container">
        <FormLabel>Logo</FormLabel>
        <input
          ref="fileInputRef"
          type="file"
          name="file"
          accept=".jpg, .jpeg, .png"
          @change="handleFileChange"
        />
      </div>
    </el-form-item>

    <el-form-item class="form-elem-container" prop="description">
      <div class="form-elem-container">
        <FormLabel>Description</FormLabel>
        <el-input
          v-model="formData.description"
          type="textarea"
          resize="vertical"
          size="large"
          clearable
        />
      </div>
    </el-form-item>

    <el-form-item class="form-elem-container" prop="note">
      <div class="form-elem-container">
        <FormLabel>Note</FormLabel>
        <el-input
          v-model="formData.note"
          clearable
          placeholder="Please input note"
        />
      </div>
    </el-form-item>
  </div>
    <div class="feature-filter-submit">
      <NavButton
        type="button"
        @click="organizationStore.setCreateModalVisibility(false)"
      >
        Cancel</NavButton
      >
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.ORGANIZATION_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>
</template>
